import { InfiniteScroll } from "../infinite-scroll";
import { KeyValuesOf } from "../../../../../domain/entities/utils";
import { MultipleOption } from "./multiple-option";
import { Option } from "./option";
import { SelectChips } from "./chips";
import { isEmpty } from "lodash";
import { twMerge } from "tailwind-merge";
import { CaretIcon, CloseIcon } from "../../../assets";
import {
  FormControl,
  FormHelperText,
  List,
  Popover,
  TextField,
} from "@material-ui/core";
import React, { useCallback, useMemo, useState } from "react";

const numberRegex = /^[0-9]+$/;

type SelectProps<T> = {
  value: Array<T> | T | undefined;
  placeholder: string;
  required?: boolean;
  multiselect?: boolean;
  className?: string;
  classNameContainer?: string;
  rootInputProps?: Partial<{
    className: string;
  }>;
  onClose?: () => void;
  size?: "small" | "medium";
  variant?: "outlined" | "standard";
  startIcon?: React.ReactNode;
  error?: string;
  customInput?: (
    handleClick: (event: React.MouseEvent<HTMLInputElement>) => void
  ) => React.ReactElement;
  disabled?: boolean;
  onChange: (data?: T | Array<T>) => void;
  name: string;
  noError?: boolean;
  searchValue?: string;
  renderOptionComponent: (option: T) => React.ReactNode;
  noMore?: boolean;
  loading?: boolean;
  getMore?: () => void;
  options: Array<T>;
  label?: (option: T) => string;
  labelProp: KeyValuesOf<T, string>;
  asyncFilter?: (value: string) => void;
  isNumeric?: boolean;
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions,react/function-component-definition
const Select = <T extends Record<string, unknown> = Record<string, unknown>>({
  value,
  onClose,
  onChange,
  className,
  classNameContainer,
  placeholder,
  rootInputProps,
  required = false,
  size = "medium",
  error,
  customInput,
  variant = "standard",
  disabled,
  name,
  multiselect = false,
  noError = false,
  searchValue,
  renderOptionComponent,
  noMore = true,
  loading = false,
  getMore = () => null,
  options,
  label,
  labelProp,
  asyncFilter,
  isNumeric = false,
}: SelectProps<T>): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>): void => {
      !disabled && setAnchorEl(event.currentTarget);
    },
    [disabled]
  );

  const onInputChange = useMemo(
    () =>
      asyncFilter
        ? (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            asyncFilter && asyncFilter(event?.target.value)
        : undefined,
    [asyncFilter]
  );

  const onClear = useCallback(
    () => asyncFilter && asyncFilter(""),
    [asyncFilter]
  );

  const handleClose = useCallback((): void => {
    if (onClose) {
      onClose();
    }

    setAnchorEl(null);
  }, [onClose]);

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLElement>): void => {
      e.stopPropagation();
      if (!disabled) {
        onChange(multiselect ? [] : undefined);
        onClear && onClear();
      }
    },
    [onClear, disabled, multiselect, onChange]
  );

  const getOptionLabel = useCallback<(option: T) => string>(
    (option) => {
      if (label) {
        return label(option);
      }

      return option[labelProp] as string;
    },
    [label, labelProp]
  );

  const getInputValue = useCallback(() => {
    if (value) {
      if (Array.isArray(value)) {
        if (value.length > 1) {
          return `${value.length} selected`;
        }
        if (value.length === 1) {
          return getOptionLabel(value[0]);
        }

        return "";
      }

      return getOptionLabel(value);
    }

    return "";
  }, [getOptionLabel, value]);

  const input = useMemo(
    () => (
      <FormControl error={!!error} fullWidth>
        <TextField
          fullWidth
          name={name}
          error={!!error}
          placeholder={placeholder}
          value={getInputValue()}
          variant={variant}
          size={size}
          onClick={handleClick}
          inputProps={rootInputProps}
          autoComplete="off"
          InputLabelProps={{
            required,
            classes: {
              asterisk: variant === "standard" ? "hidden" : "text-red",
            },
          }}
          InputProps={{
            endAdornment: (
              <section className="grid grid-flow-col items-center gap-[5px]">
                {!isEmpty(value) && !disabled && (
                  <button onClick={handleClear}>
                    <CloseIcon
                      width={16}
                      height={16}
                      className="min-w-[1rem] text-base text-gray-800"
                    />
                  </button>
                )}
                <CaretIcon className="min-w-[1rem] text-base text-gray-800" />
              </section>
            ),
            onClick: handleClick,
            classes: {
              input:
                "border-b-solid border-0 border-b-[1px] border-b-[#CFCFCF] px-[16px] py-[10px] text-[14px] outline-0 placeholder:text-[#A3A3A3]",
            },
          }}
        />
        {!noError && (
          <React.Fragment>
            <FormHelperText
              className={twMerge(
                `ml-0 h-4 truncate text-xs font-semibold text-[#F44336] ${
                  error ? "visible" : "invisible"
                }`
              )}
            >
              {error}
            </FormHelperText>
          </React.Fragment>
        )}
        {required && variant === "standard" ? (
          <span
            className={`"top-0" absolute right-0 text-sm
              text-[#F44336]`}
          >
            *
          </span>
        ) : null}
      </FormControl>
    ),
    [
      error,
      name,
      placeholder,
      getInputValue,
      variant,
      size,
      handleClick,
      rootInputProps,
      required,
      value,
      disabled,
      handleClear,
      noError,
    ]
  );

  const renderItem = useCallback<
    (option: T, key: number) => React.ReactElement
  >(
    // eslint-disable-next-line react/display-name
    (option, key) =>
      multiselect ? (
        <MultipleOption
          key={key}
          value={value as Array<T>}
          item={option}
          getOptionLabel={renderOptionComponent}
          onChange={onChange}
        />
      ) : (
        <Option
          key={key}
          item={option}
          getOptionLabel={renderOptionComponent}
          onChange={onChange}
          close={handleClose}
        />
      ),
    [handleClose, multiselect, onChange, renderOptionComponent, value]
  );

  return (
    <div className={`relative w-full ${classNameContainer ?? ""}`}>
      {required ? (
        <span className="text-red absolute top-0 right-0 text-sm">*</span>
      ) : null}
      {customInput ? customInput(handleClick) : input}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        className="select-popover z-[1300]"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          className: "max-h-screen",
        }}
      >
        <div className={twMerge("relative p-3", className)}>
          {onInputChange ? (
            <div className="static z-50 bg-white">
              <TextField
                fullWidth
                placeholder="Search..."
                variant="outlined"
                value={searchValue}
                onChange={(e) => {
                  isNumeric
                    ? e.target.value.match(numberRegex) || e.target.value === ""
                      ? onInputChange(e)
                      : null
                    : onInputChange(e);
                }}
                autoFocus={true}
                autoComplete="off"
                InputProps={{
                  classes: {
                    root: "h-10 mb-[10px]",
                    input: "text-sm text-gray-700 p-[5px]",
                  },
                }}
              />
            </div>
          ) : null}
          {multiselect && (
            <SelectChips
              value={value as Array<T>}
              onChange={onChange}
              getOptionLabel={getOptionLabel}
            />
          )}
          <List
            className={`max-h-60 w-full overflow-hidden overflow-y-auto bg-white`}
          >
            <InfiniteScroll
              data={options}
              hasNextPage={!noMore}
              loadMore={getMore}
              renderItem={renderItem}
              isLoading={loading}
            />
          </List>
        </div>
      </Popover>
    </div>
  );
};

export { Select };
export type { SelectProps };
